<template>
  <section class="overflow-x-hidden bg-black6 pb-20 pt-20" data-testid="theme-section">
    <!-- セクションヘッダー: 左寄せタイトル(gold) + 矢印 + gold水平線、サブタイトル
         Figma の SectionTitle は SP/PC とも flex gap-[16px]。SP では line を非表示にして矢印を右端に置く。
         タイトル + 矢印 + 水平線は View all と同じく体験一覧(/s/experiences/) へ遷移するリンクとして機能する。 -->
    <div class="mx-auto mb-8 flex flex-col gap-y-2 px-4 md:mb-10 md:w-11/12 md:max-w-[1216px] md:px-0">
      <h2 class="font-Spectral text-xl font-medium tracking-[0.5px] text-gold4 md:text-[30px] md:leading-[40px]">
        <button class="flex w-full items-center gap-x-4 text-left" data-testid="theme-section-header" type="button" @click="handleClickViewAll">
          <span class="flex-1 md:flex-initial">Explore Japanese Cultural Experiences</span>
          <img alt="" aria-hidden="true" class="h-6 w-6 shrink-0" :src="arrowRightGold500Icon" />
          <span aria-hidden="true" class="hidden h-[0.5px] flex-grow bg-gold4 md:block" />
        </button>
      </h2>
      <p class="font-Spectral text-base leading-6 text-stone-400 md:text-[17px]">Meaningful moments that become part of your life's lasting story.</p>
    </div>

    <!-- Mosaic grid (Z-pattern flow):
         Desktop: 4-col x 4-row グリッド。横幅いっぱいまで広げる(余白なし)。
           - 大カード(sword) を col 1-2 / row 1-2 (左上)
           - 大カード(zen)   を col 3-4 / row 3-4 (右下)
           - 残り 8 件は 2x2 ずつのサブグリッドに収まる
         Mobile : 2-col グリッドで Z 順を維持。
         CSS Grid auto-flow は row-first/sparse なので、各カードに明示の col-start/row-start を指定して
         配置ズレを防ぐ。 -->
    <div class="hidden w-full md:grid md:grid-cols-4 md:grid-rows-4">
      <NuxtLink
        v-if="topLeftLargeTheme"
        :key="topLeftLargeTheme.id"
        class="group relative col-span-2 row-span-2 aspect-[3/2] w-full overflow-hidden bg-black1 md:col-start-1 md:row-start-1"
        :data-testid="`theme-card-${topLeftLargeTheme.id}`"
        :to="topLeftLargeTheme.detailPageLink"
        @click="handleClickTheme(topLeftLargeTheme)"
      >
        <ThemeImage variant="large" :alt="topLeftLargeTheme.titleEn" :src="topLeftLargeTheme.backgroundUrl" />
        <ThemeTitleOverlay>{{ topLeftLargeTheme.titleEn }}</ThemeTitleOverlay>
      </NuxtLink>

      <NuxtLink
        v-for="(theme, i) in topRightSmallThemes"
        :key="theme.id"
        :class="['group relative aspect-[3/2] w-full overflow-hidden bg-black1', topRightSlotClasses[i]]"
        :data-testid="`theme-card-${theme.id}`"
        :to="theme.detailPageLink"
        @click="handleClickTheme(theme)"
      >
        <ThemeImage variant="small" :alt="theme.titleEn" :src="theme.backgroundUrl" />
        <ThemeTitleOverlay>{{ theme.titleEn }}</ThemeTitleOverlay>
      </NuxtLink>

      <NuxtLink
        v-for="(theme, i) in bottomLeftSmallThemes"
        :key="theme.id"
        :class="['group relative aspect-[3/2] w-full overflow-hidden bg-black1', bottomLeftSlotClasses[i]]"
        :data-testid="`theme-card-${theme.id}`"
        :to="theme.detailPageLink"
        @click="handleClickTheme(theme)"
      >
        <ThemeImage variant="small" :alt="theme.titleEn" :src="theme.backgroundUrl" />
        <ThemeTitleOverlay>{{ theme.titleEn }}</ThemeTitleOverlay>
      </NuxtLink>

      <NuxtLink
        v-if="bottomRightLargeTheme"
        :key="bottomRightLargeTheme.id"
        class="group relative col-span-2 row-span-2 aspect-[3/2] w-full overflow-hidden bg-black1 md:col-start-3 md:row-start-3"
        :data-testid="`theme-card-${bottomRightLargeTheme.id}`"
        :to="bottomRightLargeTheme.detailPageLink"
        @click="handleClickTheme(bottomRightLargeTheme)"
      >
        <ThemeImage variant="large" :alt="bottomRightLargeTheme.titleEn" :src="bottomRightLargeTheme.backgroundUrl" />
        <ThemeTitleOverlay>{{ bottomRightLargeTheme.titleEn }}</ThemeTitleOverlay>
      </NuxtLink>
    </div>

    <!-- SP: 横スクロールカルーセル。他セクションと同じ MoleculesHorizontalScrollWrapper を使用し、
         スクロールバーは共通の .slide-scrollbar（SP: 細い金1px / DT(≥992px): 非表示）と内部 gap-2 を共通化する。
         最初のカードにのみ ml-4 を付与してヘッダーと同じ 16px 左パディングを実現する。 -->
    <MoleculesHorizontalScrollWrapper class="md:hidden" scroll-button-box-style="h-[400px]">
      <template v-for="(theme, i) in displayThemes" :key="theme.id">
        <NuxtLink
          :class="['group relative h-[400px] w-[320px] shrink-0 overflow-hidden bg-black1', i === 0 ? 'ml-4' : '']"
          :data-testid="`theme-card-${theme.id}`"
          :to="theme.detailPageLink"
          @click="handleClickTheme(theme)"
        >
          <!-- CMS 画像のみ変換を通す。noImage はバンドルSVGのため変換に通さず素の <img> で出す -->
          <OptimizedImage
            v-if="theme.backgroundUrl"
            class="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
            :alt="theme.titleEn || 'no-image'"
            :image-url="theme.backgroundUrl"
            :optimized-image-options="{ width: 640 }"
          />
          <img v-else class="absolute inset-0 h-full w-full object-cover" loading="lazy" :alt="theme.titleEn || 'no-image'" :src="noImage" />
          <div class="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-b from-transparent to-black px-2 py-4">
            <span class="font-Spectral text-base leading-6 text-white">{{ theme.titleEn }}</span>
            <img alt="" aria-hidden="true" class="h-2.6 w-2.6 shrink-0" :src="arrowRightWhiteIcon" />
          </div>
        </NuxtLink>
      </template>
    </MoleculesHorizontalScrollWrapper>

    <div class="mt-8 flex justify-center px-4 md:mt-10 md:px-0">
      <button
        class="flex h-12 w-full max-w-[368px] items-center justify-center bg-dark-bg-accent px-6 font-Spectral text-[17px] text-gray9 transition-opacity hover:opacity-90"
        data-testid="view-all-experiences-btn"
        type="button"
        @click="handleClickViewAll"
      >
        <span>View all experiences</span>
      </button>
    </div>
  </section>
</template>

<script lang="ts" setup>
import arrowRightGold500Icon from '@/assets/icon/arrow/right-gold500.svg';
import arrowRightWhiteIcon from '@/assets/icon/arrow/right-white.svg';
import noImage from '@/assets/img/no-image.svg';
import MoleculesHorizontalScrollWrapper from '@/components/molecules/HorizontalScrollWrapper/HorizontalScrollWrapper.vue';
import OptimizedImage from '@/wabunka-src/common/components/stateful/OptimizedImage/OptimizedImage.vue';
import {
  generateDetailPageLink,
  getBackgroundImageUrl,
  type MinimumFeaturedListViewModel,
  type MinimumFeaturedViewModel,
} from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';
import ThemeImage from '@/wabunka-src/features/Top/pages/components/ThemeSection/ThemeImage.vue';
import ThemeTitleOverlay from '@/wabunka-src/features/Top/pages/components/ThemeSection/ThemeTitleOverlay.vue';

// Figma 仕様（10件・Z字配置）に合わせた表示上限。
// 10件未満の場合、PC グリッドの一部スロットが空になる（テンプレートは v-if/v-for でガード済みでクラッシュはしない）。
// admin で常時 10 件供給されることを前提とする。
const MAX_DISPLAY_THEMES = 10;

interface Props {
  themeList: MinimumFeaturedListViewModel;
  onThemeClick?: (theme: MinimumFeaturedViewModel) => void;
  onClickViewAll?: () => void;
}

const props = defineProps<Props>();

// テンプレート内での関数呼び出しを避けるため、リンク URL と背景画像 URL を事前計算する
type DisplayTheme = MinimumFeaturedViewModel & {
  detailPageLink: string;
  backgroundUrl: string;
};

const displayThemes = computed<DisplayTheme[]>(() =>
  props.themeList.slice(0, MAX_DISPLAY_THEMES).map((theme) => ({
    ...theme,
    detailPageLink: generateDetailPageLink(theme),
    backgroundUrl: getBackgroundImageUrl(theme),
  })),
);

// Z字配置 (Figma node 58272:16297):
// 1=topLeftLarge / 2-5=topRightSmall / 6-9=bottomLeftSmall / 10=bottomRightLarge
const topLeftLargeTheme = computed<DisplayTheme | undefined>(() => displayThemes.value[0]);
const topRightSmallThemes = computed<DisplayTheme[]>(() => displayThemes.value.slice(1, 5));
const bottomLeftSmallThemes = computed<DisplayTheme[]>(() => displayThemes.value.slice(5, 9));
const bottomRightLargeTheme = computed<DisplayTheme | undefined>(() => displayThemes.value[9]);

// PC グリッドの slot 位置（左上→右下に Z 字で読む）。
// Tailwind の purge 対策で静的文字列として保持する。
const topRightSlotClasses = ['md:col-start-3 md:row-start-1', 'md:col-start-4 md:row-start-1', 'md:col-start-3 md:row-start-2', 'md:col-start-4 md:row-start-2'] as const;
const bottomLeftSlotClasses = ['md:col-start-1 md:row-start-3', 'md:col-start-2 md:row-start-3', 'md:col-start-1 md:row-start-4', 'md:col-start-2 md:row-start-4'] as const;

const handleClickTheme = (theme: MinimumFeaturedViewModel) => {
  props.onThemeClick?.(theme);
};

const handleClickViewAll = () => {
  props.onClickViewAll?.();
};
</script>
